/* One deck card — the v49 card grammar, v52 ledger rule (specs of
 * record: proposals-design-v49-2026-08-08.html +
 * proposals-design-v52-2026-08-09.html).
 *
 * ctit "Stage · role" · unified movements/balance headers · "Σ to <as-of>"
 * rows · v52: NO difference row — ✓/✗ ride a dedicated narrow end column
 * so the amounts never move, and the residual figure rides the
 * closes-with plug row alone · plug zone dashed rust with Book folded
 * into the line · objects band · edit mode (per-row checkbox + reject +
 * leave-pending) · snooze menu · ⋯ verbs · gated card = red "gated by ↑"
 * line with INVISIBLE body · question card with field-survivor picks ·
 * one-size field-change card (attributes only). */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { ApiError } from '../../lib/api'
import { formatNumberString, humanizeTokensIn } from '../../lib/officerFormat'
import {
  approveDocs,
  bookPlug,
  confirmPreExisting,
  keepSeparate,
  markFundVehicle,
  mergeObjects,
  resolveTradeDuplicate,
  resolveWalk,
  snoozeTask,
} from './api'
import type { TradeDuplicateOutcome } from './api'
import type { ApproveDocsRow, CardRow, DeckCard, DeckResponse, WalkOutcome } from './types'
import RkCandidates from './RkCandidates'
import { decideSides } from './decideSides'
// the coach scrims are cut (audit cut 1) — the help dot carries the words
import { HelpDot } from '../guidance/Guidance'
import { translateText, useTranslationEnabled } from '../translate/api'

export interface OpenDocPayload {
  title: string
  documentId?: string | null
  page?: number | null
  snippet?: string | null
  highlight?: string | null
  /** Source document language (W4): non-'en' offers translate-to-English. */
  language?: string | null
  proposalId?: string | null
}

interface Props {
  card: DeckCard
  deck: DeckResponse
  onDone: (slimText: string) => void
  onOpenDoc: (payload: OpenDocPayload) => void
  /** Refetch the deck WITHOUT settling this card — for verbs that change
   * server state but leave the card's rows pending (mark-as-fund). */
  onRefresh?: () => void
  onPendingChange?: (pending: boolean) => void
  allowedProposalIds?: readonly string[]
}

const STATE_DOT: Record<string, string> = {
  question: 'd-q',
  inferred: 'd-inf',
  new: 'd-new',
  update: 'd-upd',
  recorded: 'd-rec',
}

function rowSection(r: CardRow): 'field' | 'balance' | 'movement' | 'object' {
  if (r.field) return 'field'
  if (r.row_id.startsWith('balance:')) return 'balance'
  // A new instrument is a thing that will exist, not a movement (Ale
  // 2026-08-19) — it books from the OBJECTS section beside the entities.
  if (r.row_id.startsWith('instrument:')) return 'object'
  return 'movement'
}

function evidenceTitle(r: CardRow): string {
  return r.evidence?.page ? `Source document · p.${r.evidence.page}` : 'Source document'
}

function rowHighlight(r: CardRow): string {
  let hl = r.label
  if (r.date) hl += ` · ${r.date}`
  if (r.amount) hl += ` · ${r.amount}`
  if (r.proposed_value) hl += ` · ${r.proposed_value}`
  return hl
}

function errDetail(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { detail?: unknown } | undefined
    if (body && typeof body.detail === 'string') return body.detail
    return `${err.status} ${err.statusText}`
  }
  return err instanceof Error ? err.message : 'request failed'
}

export default function RkCard({
  card,
  deck,
  onDone,
  onOpenDoc,
  onRefresh,
  onPendingChange,
  allowedProposalIds,
}: Props) {
  const [editing, setEditing] = useState(false)
  const [checked, setChecked] = useState<Record<string, boolean>>({})
  const [rejected, setRejected] = useState<Record<string, boolean>>({})
  const [menuOpen, setMenuOpen] = useState<'' | 'more' | 'zz'>('')
  /* Walk card (L2): TWO options, the follow-up INSIDE the chosen one,
   * ONE Confirm. `walkPick` is the chosen option; `walkReason` the
   * terminal reason inside it; `walkNote` the justification. */
  const [walkPick, setWalkPick] = useState<'' | 'stop' | 'go'>('')
  const [walkReason, setWalkReason] = useState<string>('')
  const [walkNote, setWalkNote] = useState('')
  const [mergeOpen, setMergeOpen] = useState(false)
  const [survivorName, setSurvivorName] = useState<'record' | 'proposed'>('record')
  /* D-2 (typed reason): the officer's own words, per rejected row. There
   * is deliberately NO fallback string — a machine-authored justification
   * recorded under the officer's name is worse than none, because it
   * reads as deliberate. Empty ⇒ the verb stays disarmed. */
  const [reasons, setReasons] = useState<Record<string, string>>({})
  const [discardOpen, setDiscardOpen] = useState(false)
  const [discardReason, setDiscardReason] = useState('')
  /* §914a Tier-3: the free text IS the attestation — same rule. */
  const [attestOpen, setAttestOpen] = useState(false)
  const [attestation, setAttestation] = useState('')
  /* F2: which candidate of the ranked box the merge targets. */
  const [targetIdx, setTargetIdx] = useState(0)
  /* F3: which of the issuer's recorded instruments this create links to.
   * null = nothing preselected — Approve still creates it as new. */
  const [issuerPick, setIssuerPick] = useState<number | null>(null)
  /* FIND #4 (walk 2026-08-19, Ale ruling: apply): with candidates on the
   * issuer panel and NOTHING picked, Approve silently minted a NEW
   * instrument — the exact twin the panel exists to prevent. First
   * Approve click arms this warning; the second proceeds deliberately. */
  const [newInstrumentWarned, setNewInstrumentWarned] = useState(false)
  /* Twin-guard 409 escape hatch (review catch on #1120): the guard's
   * remedies must be expressible on THIS surface. When the server
   * refuses a blanket approve because a row repeats a booked trade, the
   * blocked proposal ids (parsed from the refusal) arm a deliberate
   * "book anyway — distinct event" resubmit with action 'resolve'. */
  const [twinBlockedIds, setTwinBlockedIds] = useState<string[]>([])
  /* Snapshot-isolation refusal (Ale 2026-09-02): the server returns 409
   * deck_changed when a document processed while the officer was on this
   * card — nothing booked. We arm a "Reload card" affordance rather than
   * a retry, because the officer must SEE what changed before re-deciding. */
  const [deckChanged, setDeckChanged] = useState(false)
  const [busy, setBusy] = useState(false)
  const pendingCallback = useRef(onPendingChange)
  pendingCallback.current = onPendingChange
  useEffect(() => {
    pendingCallback.current?.(busy)
  }, [busy])
  useEffect(() => () => pendingCallback.current?.(false), [])
  const [error, setError] = useState<string | null>(null)
  /* Success flash for non-settling verbs (mark-as-fund): the card stays
   * live — its rows are still pending — so the confirmation renders in
   * place rather than settling the card. */
  const [notice, setNotice] = useState<string | null>(null)
  /* W4 (Ale ruling 2026-08-21): extracted-object names get the EN
   * affordance too — display-only. `tl()` swaps row/object labels from a
   * per-card map; stored identity, booking payloads and the resolver
   * never see translated strings. */
  const translationOn = useTranslationEnabled()
  const [labelsEn, setLabelsEn] = useState(false)
  const [labelMap, setLabelMap] = useState<Record<string, string>>({})
  const [translatingLabels, setTranslatingLabels] = useState(false)

  const rows = useMemo(() => card.rows ?? [], [card.rows])
  const objects = useMemo(() => card.objects ?? [], [card.objects])

  /* Cold-start founding verb (2026-08-18): with rk_decks ON the
   * ChainReviewPage home of "Mark as fund" is unreachable, so the deck's
   * entity object row carries it. Idempotent server-side; the row's
   * fund_vehicle_candidate flag disappears once the Fund exists. */
  const markFund = async (o: CardRow) => {
    if (!deck.group_id || !o.proposal_id) return
    setBusy(true)
    setError(null)
    try {
      const r = await markFundVehicle(deck.group_id, o.proposal_id)
      // NOT onDone: the Fund now exists, but this card's rows are still
      // pending — the officer approves them next. Settling here painted
      // "all settled" over pending proposals (walk catch, 2026-08-19).
      setNotice(
        r.created
          ? `${r.fund_name} materialized as a fund — it now heads its chain. Now approve the rows.`
          : `${r.fund_name} already exists as a fund — nothing new created.`
      )
      onRefresh?.()
    } catch (err) {
      setError(errDetail(err))
    } finally {
      setBusy(false)
    }
  }
  const actions = card.actions ?? []
  const movementRows = rows.filter((r) => rowSection(r) === 'movement')
  const balanceRows = rows.filter((r) => rowSection(r) === 'balance')
  const fieldRows = rows.filter((r) => rowSection(r) === 'field')
  const instrumentObjectRows = rows.filter((r) => rowSection(r) === 'object')
  const allObjects = instrumentObjectRows.concat(objects)
  const isFieldCard = fieldRows.length > 0 && movementRows.length === 0 && balanceRows.length === 0
  const tl = (t: string) => (labelsEn ? (labelMap[t] ?? t) : t)
  const offerLabelEn =
    translationOn === true && !!card.source_language && card.source_language !== 'en'
  const toggleLabelEn = async () => {
    if (labelsEn) {
      setLabelsEn(false)
      return
    }
    if (Object.keys(labelMap).length) {
      setLabelsEn(true)
      return
    }
    const labels = Array.from(
      new Set(
        rows
          .concat(objects)
          .map((r) => r.label)
          .filter(Boolean)
      )
    )
    setTranslatingLabels(true)
    try {
      const entries = await Promise.all(
        labels.map(async (l) => [l, (await translateText(l)).translated] as const)
      )
      setLabelMap(Object.fromEntries(entries))
      setLabelsEn(true)
    } catch {
      /* a failed translate leaves the originals — nothing to report */
    } finally {
      setTranslatingLabels(false)
    }
  }
  const [openObjects, setOpenObjects] = useState<Record<string, boolean>>({})

  const bookableRows = rows.concat(objects).filter((r) => r.bookable && r.proposal_id)

  const isChecked = (id: string) => checked[id] ?? true
  const isRejected = (id: string) => rejected[id] ?? false

  /* superseded manual rows (v44): the documented row re-states the manual
   * entry in place — strike the manual twin "superseded — the document
   * rules". The manual twin is not itself a payload row, so the strike
   * renders on rows that DECLARE a supersession, as a struck context line
   * above them when the API provides one via a recorded row. */
  const supersededIds = new Set(
    rows.filter((r) => r.supersedes_trade_id).map((r) => `trade-record:${r.supersedes_trade_id}`)
  )

  function toggleReject(id: string) {
    setRejected((prev) => ({ ...prev, [id]: !isRejected(id) }))
    setChecked((prev) => ({ ...prev, [id]: isRejected(id) }))
  }

  function cancelEdit() {
    setEditing(false)
    setChecked({})
    setRejected({})
    setReasons({})
    setDiscardOpen(false)
    setDiscardReason('')
  }

  const reasonOf = (id: string) => (reasons[id] ?? '').trim()
  /* Every rejected row owes a typed reason before the selective verb arms
   * (D-2). This is the whole of F1: no prompt, no default, no silent
   * fallback — an unreasoned rejection simply cannot be submitted. */
  const rejectedRowIds = bookableRows.map((r) => r.row_id).filter((id) => isRejected(id))
  const reasonsMissing = rejectedRowIds.some((id) => !reasonOf(id))

  async function run(fn: () => Promise<void>) {
    setBusy(true)
    setError(null)
    setTwinBlockedIds([])
    setDeckChanged(false)
    try {
      await fn()
    } catch (err) {
      const detail = errDetail(err)
      if (detail.includes('deck_changed')) {
        // Strip the wire prefix — the officer sees the sentence, not the code.
        setError(detail.replace(/^deck_changed:\s*/, ''))
        setDeckChanged(true)
      } else {
        setError(detail)
        if (detail.includes('repeats a booked trade')) {
          const ids = [...detail.matchAll(/proposal ([0-9a-f-]{36})/g)].map((m) => m[1])
          setTwinBlockedIds(ids)
        }
      }
    } finally {
      setBusy(false)
    }
  }

  async function submitBookAnyway() {
    if (!deck.group_id || !twinBlockedIds.length) return
    if (allowedProposalIds && twinBlockedIds.some((id) => !allowedProposalIds.includes(id))) {
      setError(
        'This retry includes work outside the selected review. Reload the card before continuing.'
      )
      return
    }
    const payload: ApproveDocsRow[] = twinBlockedIds.map((pid) => ({
      proposal_id: pid,
      action: 'resolve' as const,
    }))
    await run(async () => {
      await approveDocs(deck.group_id!, payload, deck.deck_version)
      onDone(
        `✓ booked deliberately — ${payload.length} repeated-key row${payload.length > 1 ? 's' : ''}`
      )
    })
  }

  /* ONE shared error block for every card branch (sixth-review lesson:
   * per-branch placement kept missing surfaces). The book-anyway hatch
   * rides WITH the error wherever it renders; it arms only when a
   * twin-guard refusal supplied blocked ids, which only approveDocs
   * branches can produce. */
  const errorBlock =
    error || notice ? (
      <>
        {notice ? (
          <div className="noticeline" data-testid="card-notice">
            {notice}
          </div>
        ) : null}
        {twinBlockedIds.length ? (
          <button
            type="button"
            className="btn p"
            data-testid="twin-book-anyway"
            disabled={busy}
            onClick={submitBookAnyway}
          >
            Book anyway — this is a distinct event
          </button>
        ) : null}
        {error ? (
          <div className="errline" data-testid="card-error">
            {error}
          </div>
        ) : null}
        {deckChanged ? (
          /* Reason first, then the action (perception look 2026-09-02: the
           * button used to sit above the red line it answers, and the ?'s
           * popover then covered that line). The claims card already reads
           * reason → Reload; this matches it. */
          <>
            <button
              type="button"
              className="btn p"
              data-testid="deck-changed-reload"
              disabled={busy}
              onClick={() => {
                setError(null)
                setDeckChanged(false)
                onRefresh?.()
              }}
            >
              Reload card to see what changed
            </button>
            {/* Guide: the refusal is a new situation for the officer — the ?
             * explains what happened and what Reload does. */}
            <HelpDot k="deck.changed" />
          </>
        ) : null}
      </>
    ) : null

  /* ── booking verbs ─────────────────────────────────────────────── */

  const booksSummary = useMemo(() => {
    if (isFieldCard)
      return (
        <>
          Approve books: <b>{fieldRows.length} field updates</b> · old values kept in history.
        </>
      )
    const names = rows.filter((r) => r.bookable && r.proposal_id).map((r) => r.label.toLowerCase())
    const parts: ReactNode[] = []
    if (names.length)
      parts.push(
        <b key="rows">{names.length > 2 ? `${names.length} claims` : names.join(' + ')}</b>
      )
    const objRows = objects.filter((o) => o.bookable && o.proposal_id)
    if (objRows.length)
      parts.push(
        <span key="objs">
          {parts.length ? ' + ' : ''}
          <b>
            {objRows.length > 3
              ? `${objRows.length} objects`
              : objRows.map((o) => o.label).join(' + ')}
          </b>
        </span>
      )
    if (!parts.length) return null
    return <>Approve books: {parts}.</>
  }, [isFieldCard, fieldRows.length, rows, objects])

  async function submitApprove(selective: boolean) {
    if (!deck.group_id) return
    if (
      card.issuer_panel &&
      card.issuer_panel.instruments.length > 0 &&
      issuerPick === null &&
      !newInstrumentWarned
    ) {
      setNewInstrumentWarned(true)
      setNotice(
        `Nothing picked — this creates “${card.issuer_panel.proposed_name}” as a NEW ` +
          `instrument beside the issuer's existing ones. Pick a candidate to fold onto it, ` +
          `or click Approve again to create it deliberately.`
      )
      return
    }
    // D-2: refuse to submit rather than invent a justification.
    if (selective && reasonsMissing) return
    /* ONE decision per proposal (the server 400s on duplicates) — field
     * cards render several rows off a single proposal (walk catch,
     * 2026-08-19: field-card Approve always died on the duplicate check).
     * Precedence when rows of one proposal disagree: reject > approve >
     * leave_pending. */
    const byProposal = new Map<string, ApproveDocsRow>()
    const rank = { reject: 3, resolve: 2, approve: 1, leave_pending: 0 } as const
    for (const r of bookableRows) {
      const id = r.row_id
      const pid = r.proposal_id!
      let row: ApproveDocsRow
      if (selective && isRejected(id)) {
        row = { proposal_id: pid, action: 'reject', rejection_justification: reasonOf(id) }
      } else if (selective && !isChecked(id)) {
        row = { proposal_id: pid, action: 'leave_pending' }
      } else {
        row = { proposal_id: pid, action: 'approve' }
      }
      const prev = byProposal.get(pid)
      if (!prev || rank[row.action] > rank[prev.action]) byProposal.set(pid, row)
    }
    const payload: ApproveDocsRow[] = [...byProposal.values()]
    if (!payload.length) {
      // Never a silent no-op: say why the click changed nothing.
      setNotice('Nothing to book on this card — every row is already settled.')
      return
    }
    await run(async () => {
      await approveDocs(deck.group_id!, payload, deck.deck_version)
      const booked = payload.filter((p) => p.action === 'approve').length
      onDone(`✓ booked — ${booked} of ${payload.length} rows`)
    })
  }

  /* The Discard verb (D-2's other half, absent from the deck until now):
   * every row on the card rejected in ONE move, on ONE typed reason. */
  async function submitDiscard() {
    if (!deck.group_id || !discardReason.trim()) return
    const payload: ApproveDocsRow[] = [...new Set(bookableRows.map((r) => r.proposal_id!))].map(
      (pid) => ({
        proposal_id: pid,
        action: 'reject' as const,
        rejection_justification: discardReason.trim(),
      })
    )
    if (!payload.length) return
    await run(async () => {
      // Discard is a booking decision (it rejects every bookable row), so it
      // carries the same snapshot guard as Approve — a stale card is refused
      // 409 and run() arms the Reload affordance (claude-review catch).
      await approveDocs(deck.group_id!, payload, deck.deck_version)
      setDiscardOpen(false)
      onDone(`✓ discarded — ${payload.length} row${payload.length > 1 ? 's' : ''}, reason recorded`)
    })
  }

  async function submitBookPlug() {
    if (!card.task_id) return
    await run(async () => {
      await bookPlug(card.task_id!)
      const requests = card.shape !== 'derived_balance'
      onDone(requests ? '✓ reconciled · document now owed on the entry' : '✓ reconciled')
    })
  }

  /* §914a Tier-3: the attestation is the officer's own statement that the
   * holding pre-dates fund involvement. It is typed, never defaulted —
   * a canned string signed by a named officer is a false audit record. */
  async function submitConfirmPreExisting() {
    if (!card.task_id) return
    if (!attestOpen) {
      setAttestOpen(true)
      return
    }
    if (!attestation.trim()) return
    await run(async () => {
      await confirmPreExisting(card.task_id!, attestation.trim())
      setAttestOpen(false)
      onDone('✓ booked — inception balance')
    })
  }

  async function submitSnooze(mode: 'next' | '7d' | 'date') {
    if (!card.task_id) return
    setMenuOpen('')
    if (mode === 'next') {
      await run(async () => {
        await snoozeTask(card.task_id!, 'next_document')
        onDone('snoozed — wakes on the next document')
      })
    } else if (mode === '7d') {
      await run(async () => {
        await snoozeTask(card.task_id!, 'timer')
        onDone('snoozed 7 days')
      })
    } else {
      const until = window.prompt('Snooze until (YYYY-MM-DD)', '')
      if (!until) return
      await run(async () => {
        await snoozeTask(card.task_id!, 'timer', new Date(`${until}T00:00:00Z`).toISOString())
        onDone(`snoozed to ${until}`)
      })
    }
  }

  /* ── walk verbs (the look-through node decision) ───────────────── */

  const walk = card.walk
  const chosenReason = walk?.stop_reasons.find((r) => r.code === walkReason) ?? null
  const walkOutcome: WalkOutcome | null =
    walkPick === 'go' ? 'walk_through' : (walkReason as WalkOutcome) || null
  const walkReady =
    walkPick === 'go' ||
    (walkPick === 'stop' &&
      !!chosenReason &&
      (!chosenReason.justification_required || walkNote.trim().length > 0))

  async function submitWalk() {
    if (!card.task_id || !walkOutcome) return
    await run(async () => {
      const res = await resolveWalk(card.task_id!, walkOutcome, walkNote.trim() || undefined)
      if (walkOutcome === 'walk_through') {
        onDone(
          res.chain_incomplete
            ? '✓ continues through this node — chain incomplete, request it from Chase & Request'
            : '✓ continues through this node'
        )
      } else {
        onDone(
          `✓ closed — ${(chosenReason?.label ?? walkOutcome).toLowerCase()}` +
            (res.anomaly_raised ? ' · Art 19(9) anomaly report raised' : '')
        )
      }
    })
  }

  async function submitLeaveOpen() {
    setMenuOpen('')
    if (!card.task_id) return
    await run(async () => {
      await resolveWalk(card.task_id!, 'pending_review', walkNote.trim() || undefined)
      onDone('left open — pending review')
    })
  }

  /* ── decide verbs ──────────────────────────────────────────────── */

  const q = card.question
  /* F2 — the WHOLE ranked box, nominee first. The backend has always
   * carried every candidate; the card used to read `candidates[0]` and
   * nothing else, so the officer could not retarget. That is verbatim the
   * 2026-07-25 demo dry run: the fixed pick was wrong in 6 of 6 samples,
   * leaving "Keep separate" — which MINTS THE TWIN — as the only
   * non-corrupting answer. `merge-picks` chooses which NAME survives;
   * `merge-target-picks` chooses which OBJECT it merges into. */
  const candidates = useMemo(
    () => (q?.candidates ?? []) as Record<string, unknown>[],
    [q?.candidates]
  )
  const candidate = candidates[Math.min(targetIdx, Math.max(0, candidates.length - 1))]
  const nameOf = (c: Record<string, unknown> | undefined): string | null =>
    (c?.name as string | undefined) ?? (c?.canonical_name as string | undefined) ?? null
  const candidateName = nameOf(candidate)

  function targetOf(c: Record<string, unknown> | undefined): {
    target_entity_id?: string
    target_instrument_id?: string
    target_proposal_ids?: string[]
  } | null {
    if (!c) return null
    if (typeof c.entity_id === 'string') return { target_entity_id: c.entity_id }
    if (typeof c.instrument_id === 'string') return { target_instrument_id: c.instrument_id }
    if (typeof c.proposal_id === 'string') return { target_proposal_ids: [c.proposal_id] }
    if (typeof c.id === 'string') return { target_entity_id: c.id }
    return null
  }
  const mergeTarget = targetOf(candidate)

  /** A candidate's distinguishing detail, so the picker is choosable —
   * two rows reading only "Kronos Treuhand AG" are not a choice. */
  function candidateDetail(c: Record<string, unknown>): string {
    return Object.entries(c)
      .filter(
        ([k, v]) =>
          typeof v === 'string' &&
          v &&
          !['name', 'canonical_name', 'entity_id', 'instrument_id', 'proposal_id', 'id'].includes(k)
      )
      .map(([, v]) => String(v))
      .join(' · ')
  }

  async function submitMerge() {
    if (!q?.proposal_id || !mergeTarget) return
    await run(async () => {
      await mergeObjects({
        source_proposal_ids: [q.proposal_id!],
        ...mergeTarget,
        canonical_name: survivorName === 'record' ? (candidateName ?? undefined) : q.label,
      })
      onDone('✓ same — merged, alias learned')
    })
  }

  async function submitKeepSeparate() {
    if (!q?.proposal_id) return
    await run(async () => {
      await keepSeparate([q.proposal_id!])
      onDone('✓ different — kept separate')
    })
  }

  /* F3 — the link verb the standing affordance arms. Same ruled endpoint
   * the identity question uses; the deck adds no new write path. */
  async function submitIssuerLink() {
    const panel = card.issuer_panel
    if (!panel || issuerPick === null) return
    const target = panel.instruments[issuerPick]
    if (!target) return
    await run(async () => {
      await mergeObjects({
        source_proposal_ids: [panel.proposal_id],
        target_instrument_id: target.instrument_id,
        canonical_name: survivorName === 'record' ? target.name : panel.proposed_name,
      })
      onDone('✓ linked — the issuer already had this instrument')
    })
  }

  /* ── the R7 near-match answer (audit F5) ───────────────────────────
   * Two DOCUMENTED trades. Both answers are offered, neither is
   * preselected, and the handler 422s on a missing outcome — auto-merge
   * (silently understated holding) and auto-duplicate (loud value break)
   * are both forbidden, so the officer decides. */
  async function submitDuplicate(outcome: TradeDuplicateOutcome) {
    if (!card.task_id) return
    await run(async () => {
      await resolveTradeDuplicate(card.task_id!, outcome)
      onDone(
        outcome === 'same_trade'
          ? '✓ same trade — the document corroborates the trade on record'
          : '✓ new trade — booked separately as its own trade'
      )
    })
  }

  /* ── shared fragments ──────────────────────────────────────────── */

  /* The walk is a DECIDE in the officer's language (the v6 mock's card
   * chip reads "Decide · look-through") even though it is the deck's
   * fourth stage — it is still a decision about the record. */
  const stageLabel =
    card.stage === 'decide' || card.stage === 'walk'
      ? 'Decide'
      : card.stage === 'book'
        ? 'Book'
        : 'Reconcile'
  const kClass =
    card.stage === 'decide' || card.stage === 'walk'
      ? 'k q'
      : card.stage === 'reconcile'
        ? 'k r'
        : 'k'

  /* #816 — the advisory belongs WHERE THE DECISION IS MADE. It survived
   * only on the document page; the officer approving figures read out of
   * a French or German document on the deck was never told. */
  const langChip =
    card.source_language && card.source_language !== 'en' ? (
      <>
        <span className="langchip" data-testid="source-language-chip">
          {card.source_language.toUpperCase()} source — verify figures
        </span>
        {offerLabelEn ? (
          <button
            type="button"
            className="enlabels"
            data-testid="translate-labels"
            disabled={translatingLabels}
            onClick={() => void toggleLabelEn()}
            title={
              labelsEn
                ? 'Show the original names'
                : 'Translate the extracted names on this card to English (display only)'
            }
          >
            {translatingLabels ? '…' : labelsEn ? card.source_language.toUpperCase() : 'EN'}
          </button>
        ) : null}
      </>
    ) : null

  /** The typed-reason box (D-2). No placeholder default, no fallback. */
  const reasonBox = (id: string, label: string) => (
    <div className="reasonbox" data-testid="reject-reason" data-row-id={id}>
      <textarea
        data-testid={`reject-reason-input-${id}`}
        placeholder={label}
        value={reasons[id] ?? ''}
        onChange={(e) => setReasons((prev) => ({ ...prev, [id]: e.target.value }))}
      />
      {!reasonOf(id) ? (
        <span className="reasonhint" data-testid="reason-required">
          a reason is required — it is recorded under your name
        </span>
      ) : null}
    </div>
  )

  /* The Discard block — the verb the deck never had (F1). Opening it is
   * not discarding: the reason has to be typed before Confirm arms. */
  const discardBlock = discardOpen ? (
    <div className="reasonbox" data-testid="discard-box">
      <textarea
        data-testid="discard-reason-input"
        placeholder="why this card is discarded — recorded under your name"
        value={discardReason}
        onChange={(e) => setDiscardReason(e.target.value)}
      />
      <span className="reasonrow">
        <button
          type="button"
          className="btn p"
          data-testid="discard-confirm"
          disabled={busy || !discardReason.trim()}
          onClick={submitDiscard}
        >
          Confirm discard
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => {
            setDiscardOpen(false)
            setDiscardReason('')
          }}
        >
          Cancel
        </button>
        {!discardReason.trim() ? (
          <span className="reasonhint" data-testid="reason-required">
            a reason is required
          </span>
        ) : null}
      </span>
    </div>
  ) : null

  const discardButton =
    actions.includes('discard') && bookableRows.length ? (
      <button
        type="button"
        className="btn dsc"
        data-testid="discard"
        onClick={() => setDiscardOpen(true)}
      >
        Discard
      </button>
    ) : null

  /* The standing issuer-instrument affordance (Ale 2026-07-26 A) — shown
   * on ANY proposed instrument, flagged or not. Nothing is preselected:
   * Approve still creates it as new; picking a target arms the link. */
  const ip = card.issuer_panel
  const issuerPanel = ip ? (
    <div className="isspanel" data-testid="issuer-panel">
      <div className="issphd">
        {ip.issuer_name ?? 'This issuer'} already has {ip.instruments.length} instrument
        {ip.instruments.length > 1 ? 's' : ''} on record. Is “{ip.proposed_name}” one of them?
      </div>
      {ip.instruments.map((inst, i) => (
        <label key={inst.instrument_id} className="issrow" data-testid="issuer-instrument">
          <input
            type="radio"
            name={`ii-${card.card_id}`}
            data-instrument-id={inst.instrument_id}
            checked={issuerPick === i}
            onChange={() => {
              setIssuerPick(i)
              setNewInstrumentWarned(false)
              setNotice(null)
            }}
          />{' '}
          <span className="issname">{inst.name}</span>
          <span className="issmeta">
            {[inst.category, inst.currency, inst.supply_state].filter(Boolean).join(' · ')}
          </span>
        </label>
      ))}
      <div className="issfoot">
        {issuerPick === null ? (
          <span data-testid="issuer-nopick">
            Nothing picked — Approve records “{ip.proposed_name}” as a NEW instrument.
          </span>
        ) : (
          <>
            <button
              type="button"
              className="btn p"
              data-testid="issuer-link"
              disabled={busy}
              onClick={submitIssuerLink}
            >
              Link to this instrument instead
            </button>
            <label className="issname-elect">
              <input
                type="radio"
                name={`ie-${card.card_id}`}
                checked={survivorName === 'record'}
                onChange={() => setSurvivorName('record')}
              />{' '}
              keep “{ip.instruments[issuerPick]?.name}”
            </label>
            <label className="issname-elect">
              <input
                type="radio"
                name={`ie-${card.card_id}`}
                checked={survivorName === 'proposed'}
                onChange={() => setSurvivorName('proposed')}
              />{' '}
              rename to “{ip.proposed_name}”
            </label>
          </>
        )}
      </div>
    </div>
  ) : null

  const evidenceDocs = new Set(
    rows
      .concat(objects)
      .map((r) => r.evidence?.document_id)
      .filter((d): d is string => Boolean(d))
  )
  if (deck.document_id) evidenceDocs.add(deck.document_id)
  const srcCount = evidenceDocs.size

  function openCardSource() {
    const first = rows.concat(objects).find((r) => r.evidence)
    onOpenDoc({
      title: first ? evidenceTitle(first) : 'Source document',
      documentId: first?.evidence?.document_id ?? deck.document_id,
      proposalId: first?.proposal_id,
      page: first?.evidence?.page,
      snippet: first?.evidence?.snippet,
      highlight: first ? rowHighlight(first) : null,
      language: card.source_language,
    })
  }

  const chip = (r: CardRow) =>
    r.evidence ? (
      <button
        type="button"
        className="dch"
        aria-label="Open source clause"
        onClick={(e) => {
          e.stopPropagation()
          onOpenDoc({
            title: evidenceTitle(r),
            documentId: r.evidence?.document_id,
            proposalId: r.proposal_id,
            page: r.evidence?.page,
            snippet: r.evidence?.snippet,
            highlight: rowHighlight(r),
            language: card.source_language,
          })
        }}
      >
        📄
      </button>
    ) : null

  const bkr = (r: CardRow) => (
    <>
      <input
        type="checkbox"
        className="bk"
        checked={isChecked(r.row_id) && !isRejected(r.row_id)}
        onChange={() => setChecked((prev) => ({ ...prev, [r.row_id]: !isChecked(r.row_id) }))}
        onClick={(e) => e.stopPropagation()}
      />
      <button
        type="button"
        className="rjx"
        onClick={(e) => {
          e.stopPropagation()
          toggleReject(r.row_id)
        }}
      >
        ✕
      </button>
    </>
  )

  const lnClass = (r: CardRow, extra = '') =>
    [
      'ln',
      extra,
      r.state === 'recorded' ? 'rec' : '',
      r.bookable && r.proposal_id ? 'bkr' : '',
      isRejected(r.row_id) ? 'rejd' : '',
      supersededIds.has(r.row_id) ? 'folded' : '',
    ]
      .filter(Boolean)
      .join(' ')

  /* v52 ledger rule: ✓/✗ live in their own narrow end column so the
   * amounts never shift. A balance line is a MATCH (✓) unless the deck
   * carries a reconcile card on the same anchor — the ledger is then
   * unclosed and the balance line takes ✗; the residual figure renders
   * ONLY on that reconcile card's closes-with row, never here. */
  const anchorMismatch = deck.cards.some(
    (c) => c.stage === 'reconcile' && c.anchor_key === card.anchor_key
  )

  const endCell = (r: CardRow) =>
    rowSection(r) === 'balance' ? (
      <span className={`st4 ${anchorMismatch ? 'miss' : 'ok'}`} data-testid="match-mark">
        {anchorMismatch ? '✗' : '✓'}
      </span>
    ) : (
      <span />
    )

  const ledgerRow = (r: CardRow) => (
    <div key={r.row_id}>
      <div className={lnClass(r, r.state === 'update' ? 'stated' : '')}>
        <span className="lbl">
          {r.bookable && r.proposal_id ? bkr(r) : null}
          <span className={`sdot ${STATE_DOT[r.state]}`} />
          <span style={{ marginLeft: 9 }}>{tl(r.label)}</span>
          {r.supersedes_trade_id ? (
            <span className="supnote">supersedes your manual entry — the document rules</span>
          ) : null}
          {chip(r)}
        </span>
        <span className="dt">{r.date ?? ''}</span>
        <span className="val">{r.amount ?? ''}</span>
        {endCell(r)}
      </div>
      {isRejected(r.row_id) ? reasonBox(r.row_id, `why “${r.label}” is rejected`) : null}
    </div>
  )

  /* ── gated card: header + red line ONLY (v32 invisible body) ───── */
  if (card.gated) {
    const labels = (card.gated_by ?? []).map((g) => g.label).join(' · ')
    return (
      <div className="card popin" data-card-id={card.card_id} data-gated="true">
        <div className="ctit">
          <span className={kClass}>{stageLabel}</span>
          <span className="anch">{card.anchor_label}</span>
          <HelpDot k="deck.gated" />
        </div>
        <div className="gline" data-testid="gate-line">
          gated by ↑{labels ? ` — ${labels}` : ''}
        </div>
      </div>
    )
  }

  /* ── walk card: the look-through node (L2, v6 mock) ────────────── */
  if (card.stage === 'walk' && walk) {
    return (
      <div className="card popin walkcard" data-card-id={card.card_id} data-stage="walk">
        <div className="ctit">
          <span className={kClass}>{stageLabel}</span>
          <span className="anch">{card.anchor_label}</span>
          {walk.context_line ? (
            <span className="walkctx" data-testid="walk-context">
              {walk.context_line}
            </span>
          ) : null}
          <HelpDot k="deck.walk" />
        </div>
        {/* L4: chain_orphan / look_through_incomplete are STATUS LINES
         * here — never queue rows, never their own cards. */}
        {walk.status_lines?.length ? (
          <div className="walkstatus" data-testid="walk-status-lines">
            {walk.status_lines.map((line) => (
              <div key={line}>{line}</div>
            ))}
          </div>
        ) : null}
        <div className="qtxt">
          <b data-testid="walk-question">{walk.question}</b>
          <div className="qw" data-testid="walk-rule">
            {walk.rule_line}
          </div>
        </div>

        {/* Option A — stops here. The reason set lives INSIDE it. */}
        <div
          className={`opt${walkPick === 'stop' ? ' on' : ''}`}
          role="button"
          data-testid="walk-opt-stop"
          onClick={() => setWalkPick('stop')}
        >
          <b>Stops here</b>terminal — pick the reason it ends
        </div>
        {walkPick === 'stop' ? (
          <div className="walkstep" data-testid="walk-reasons">
            {walk.stop_reasons.map((r) => (
              <div
                key={r.code}
                className={`rr${walkReason === r.code ? ' on' : ''}`}
                role="button"
                data-reason={r.code}
                title={r.citation}
                onClick={() => setWalkReason(r.code)}
              >
                <span className="rad" />
                <span className="nmx">
                  {r.label}
                  <small>{r.detail}</small>
                </span>
                <span className="ev">
                  {r.evidence_label ? (
                    <span className={r.evidence_on_record ? 'have' : 'need'}>
                      {r.evidence_label}
                    </span>
                  ) : null}
                </span>
              </div>
            ))}
            {chosenReason?.anomaly ? (
              <div className="warnbox" data-testid="walk-anomaly-band">
                <b>Anomaly report required.</b> Closing on a no-control basis triggers a report
                under Art 19(9) — it is recorded with your name.
              </div>
            ) : null}
            {chosenReason?.justification_required ? (
              <div className="walkother">
                <textarea
                  data-testid="walk-justification"
                  placeholder="justification — stored with the decision"
                  value={walkNote}
                  onChange={(e) => setWalkNote(e.target.value)}
                />
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Option B — continues. The consequence is stated truthfully;
         * the request is OFFERED from Chase, never a second decision. */}
        <div
          className={`opt${walkPick === 'go' ? ' on' : ''}`}
          role="button"
          data-testid="walk-opt-continue"
          onClick={() => setWalkPick('go')}
        >
          <b>Continues through this node</b>the chain extends to whatever is recorded below
        </div>
        {walkPick === 'go' ? (
          <div className="walkstep">
            <div className="cont" data-testid="walk-consequence">
              {walk.continue_consequence}
            </div>
          </div>
        ) : null}

        <div className="books" data-testid="walk-books">
          {walkPick === '' ? (
            'Pick one to continue.'
          ) : walkPick === 'go' ? (
            <>
              Confirm records: <b>the chain continues through this node</b>.
            </>
          ) : chosenReason ? (
            <>
              Confirm records: <b>terminal — {chosenReason.label.toLowerCase()}</b>
              {chosenReason.anomaly ? ' · anomaly report raised' : ''}.
            </>
          ) : (
            'Choose the reason it ends.'
          )}
        </div>
        {errorBlock}
        <div className="abar">
          <button
            type="button"
            className="btn p apv"
            data-testid="walk-confirm"
            disabled={busy || !walkReady}
            onClick={submitWalk}
          >
            Confirm
          </button>
          <span className="more">
            <button
              type="button"
              className="btn"
              onClick={() => setMenuOpen(menuOpen === 'more' ? '' : 'more')}
            >
              ⋯
            </button>
            {menuOpen === 'more' ? (
              <div className="morelist" data-testid="walk-more">
                <div onClick={submitLeaveOpen}>Leave open</div>
              </div>
            ) : null}
          </span>
        </div>
      </div>
    )
  }

  /* ── decide card: the R7 near-match question (audit F5) ─────────── */
  if (card.stage === 'decide' && card.shape === 'trade_duplicate') {
    if (q?.flag === 'payment_allocation_review') {
      return (
        <div className="card popin" data-card-id={card.card_id} data-shape="payment_allocation">
          <div className="ctit">
            <span className={kClass}>{stageLabel}</span>
            <span className="anch">{card.anchor_label}</span>
            {srcCount > 0 ? (
              <button type="button" className="opensrc" onClick={openCardSource}>
                Open source 📄
              </button>
            ) : null}
          </div>
          <div className="qtxt">
            <b data-testid="allocation-question">Review the payment allocation</b>
            <div className="qw">
              The allocation is incomplete or unclear. Review the source and obtain the missing
              details before settlement is recorded.
            </div>
          </div>
          {rows.map((row) => (
            <div
              key={row.row_id}
              className="ln"
              data-testid="allocation-row"
              style={{ display: 'flex', justifyContent: 'space-between' }}
            >
              <span>{row.label}</span>
              <span>{row.proposed_value ?? '— not stated'}</span>
            </div>
          ))}
          <div className="books">
            The payment has not been allocated. No new capital trade is created.
          </div>
        </div>
      )
    }
    const differing = rows.filter((r) => r.state === 'question')
    return (
      <div className="card popin" data-card-id={card.card_id} data-shape="trade_duplicate">
        <div className="ctit">
          <span className={kClass}>{stageLabel}</span>
          <span className="anch">{card.anchor_label}</span>
          {langChip}
          <HelpDot k="deck.trade_duplicate" />
          {srcCount > 0 ? (
            <button type="button" className="opensrc" onClick={openCardSource}>
              {srcCount > 1 ? `Open sources (${srcCount}) 📄` : 'Open source 📄'}
            </button>
          ) : null}
        </div>
        <div className="qtxt">
          <b data-testid="dup-question">Is this the same trade, or a new one?</b>
          <div className="qw">
            {q?.label ?? 'A document claims a trade already on the record.'} Same holder, instrument
            and amount; the dates sit inside the near-match window.{' '}
            {differing.length
              ? `${differing.length} reading${differing.length > 1 ? 's differ' : ' differs'}.`
              : 'Every reading matches.'}
          </div>
        </div>
        <div className="hd fg">
          <span>reading</span>
          <span style={{ textAlign: 'left' }}>on record</span>
          <span></span>
          <span style={{ textAlign: 'left' }}>newly claimed</span>
        </div>
        {rows.map((r) => (
          <div key={r.row_id} className={lnClass(r, 'fg')} data-testid="dup-row">
            <span className="fname">
              <span className={`sdot ${STATE_DOT[r.state]}`} />
              <span style={{ marginLeft: 9 }}>{tl(r.label)}</span>
              {chip(r)}
            </span>
            <span className="old">{r.recorded_value ?? '— not stated'}</span>
            <span className="arr">→</span>
            <span className={r.state === 'question' ? 'new diff' : 'new'}>
              {r.proposed_value ?? '— not stated'}
            </span>
          </div>
        ))}
        <div className="books" data-testid="dup-books">
          Your answer is mandatory — neither is chosen for you. Merging silently understates the
          position; duplicating breaks the value.
        </div>
        {errorBlock}
        <div className="abar">
          <button
            type="button"
            className="btn p apv"
            data-testid="dup-same"
            disabled={busy}
            onClick={() => submitDuplicate('same_trade')}
          >
            Same trade — corroborates
          </button>
          <button
            type="button"
            className="btn"
            data-testid="dup-new"
            disabled={busy}
            onClick={() => submitDuplicate('new_trade')}
          >
            New trade — book separately
          </button>
          <span className="more">
            <button
              type="button"
              className="btn"
              onClick={() => setMenuOpen(menuOpen === 'more' ? '' : 'more')}
            >
              ⋯
            </button>
            {menuOpen === 'more' ? (
              <div className="morelist">
                <div onClick={() => setMenuOpen('')}>Leave open</div>
              </div>
            ) : null}
          </span>
        </div>
      </div>
    )
  }

  /* ── decide card ───────────────────────────────────────────────── */
  if (card.stage === 'decide' && q) {
    return (
      <div className="card popin" data-card-id={card.card_id}>
        <div className="ctit">
          <span className={kClass}>{stageLabel}</span>
          <span className="anch">{card.anchor_label}</span>
          {langChip}
          <HelpDot k="deck.identity" />
          {deck.document_id ? (
            <button type="button" className="opensrc" onClick={openCardSource}>
              Compare 📄
            </button>
          ) : null}
        </div>
        <div className="qtxt">
          <b>Same {card.anchor_role === 'instrument' ? 'instrument' : 'entity'}?</b> The document
          says “{q.label}”{candidateName ? <>; the record holds “{candidateName}”.</> : '.'}
        </div>
        {/* D1: what each side actually says, side by side — the card
         * used to ask the question and show nothing to answer it with. */}
        <RkCandidates sides={decideSides(card, targetIdx)} />
        {/* Bias to separate (R1, Ale 2026-09-04): the safe answer leads —
         * merging folds two records into one and is the harder undo. */}
        <div
          className="opt primary"
          role="button"
          data-testid="decide-keep-separate"
          onClick={submitKeepSeparate}
        >
          <b>Different — keep separate</b>
          The “{q.label}” stands as its own{' '}
          {card.anchor_role === 'instrument' ? 'instrument' : 'entity'}.
        </div>
        <div
          className="opt"
          role="button"
          data-testid="decide-merge"
          onClick={() => (mergeTarget ? setMergeOpen(true) : undefined)}
          style={mergeTarget ? undefined : { opacity: 0.5, cursor: 'default' }}
        >
          <b>Same — merge, choosing what survives</b>
          {mergeTarget
            ? 'The losing name becomes a known alias.'
            : 'No matching record to merge into here.'}
        </div>
        {mergeOpen && mergeTarget ? (
          <>
            {/* F2 — WHICH TARGET. Separate control, separate concern from
             * the surviving-name election below. Nominee first; picking an
             * alternative retargets the merge. */}
            <div className="mrgpick" data-testid="merge-target-picks">
              <div className="frow">
                <span className="k">merge into</span>
                <span>
                  {candidates.map((c, i) => {
                    const detail = candidateDetail(c)
                    return (
                      <label
                        key={`${nameOf(c) ?? 'candidate'}-${i}`}
                        data-testid="merge-target-opt"
                      >
                        <input
                          type="radio"
                          name={`mt-${card.card_id}`}
                          data-target-index={i}
                          checked={targetIdx === i}
                          onChange={() => {
                            setTargetIdx(i)
                            setSurvivorName('record')
                          }}
                        />{' '}
                        {nameOf(c) ?? 'unnamed candidate'}
                        {i === 0 ? <small className="nominee"> nominee</small> : null}
                        {detail ? <small className="cdetail"> {detail}</small> : null}
                      </label>
                    )
                  })}
                </span>
              </div>
            </div>
            <div className="mrgpick" data-testid="merge-picks">
              <div className="frow">
                <span className="k">surviving name</span>
                <span>
                  {candidateName ? (
                    <label>
                      <input
                        type="radio"
                        name={`mn-${card.card_id}`}
                        checked={survivorName === 'record'}
                        onChange={() => setSurvivorName('record')}
                      />{' '}
                      {candidateName}
                    </label>
                  ) : null}
                  <label>
                    <input
                      type="radio"
                      name={`mn-${card.card_id}`}
                      checked={survivorName === 'proposed'}
                      onChange={() => setSurvivorName('proposed')}
                    />{' '}
                    {q.label}
                  </label>
                </span>
              </div>
              {Object.entries(candidate ?? {})
                .filter(
                  ([k, v]) =>
                    typeof v === 'string' &&
                    ![
                      'name',
                      'canonical_name',
                      'entity_id',
                      'instrument_id',
                      'proposal_id',
                      'id',
                    ].includes(k)
                )
                .map(([k, v]) => (
                  <div className="frow" key={k}>
                    <span className="k">{k.replace(/_/g, ' ')}</span>
                    <span>{String(v)}</span>
                  </div>
                ))}
              <button type="button" className="btn p mrgdo" disabled={busy} onClick={submitMerge}>
                Merge with these choices
              </button>
            </div>
          </>
        ) : null}
        {errorBlock}
        <div className="abar">
          <span className="more">
            <button
              type="button"
              className="btn"
              onClick={() => setMenuOpen(menuOpen === 'more' ? '' : 'more')}
            >
              ⋯
            </button>
            {menuOpen === 'more' ? (
              <div className="morelist">
                <div onClick={() => setMenuOpen('')}>Leave open</div>
              </div>
            ) : null}
          </span>
        </div>
      </div>
    )
  }

  /* ── reconcile card: the two-leg identity check (audit F5) ──────── */
  if (card.stage === 'reconcile' && card.shape === 'leg_identity') {
    return (
      <div className="card popin" data-card-id={card.card_id} data-shape="leg_identity">
        <div className="ctit">
          <span className={kClass}>{stageLabel}</span>
          <span className="anch">{card.anchor_label}</span>
          {card.why ? (
            <span className="violchip" data-testid="leg-violation">
              {card.why.cause.replace(/_/g, ' ')}
            </span>
          ) : null}
          <HelpDot k="deck.leg_identity" />
        </div>
        <div className="hd">
          <span>legs on record</span>
          <span></span>
          <span>amount</span>
          <span></span>
        </div>
        {rows.map((r) => (
          <div key={r.row_id} className="ln rec" data-testid="leg-row">
            <span className="lbl">
              <span className={`sdot ${STATE_DOT[r.state]}`} />
              <span style={{ marginLeft: 9 }}>{tl(r.label)}</span>
            </span>
            <span className="dt"></span>
            <span className="val">{r.amount ?? ''}</span>
            <span className="st4 miss" data-testid="match-mark">
              ✗
            </span>
          </div>
        ))}
        <div className="books" data-testid="leg-books">
          {card.why?.reason ??
            'commitment = drawn + undrawn is a cross-row identity no constraint can hold.'}{' '}
          <b>Nothing to book here</b> — two stored figures disagree, so there is no
          balance-versus-movements call to make.
        </div>
        {errorBlock}
        <div className="abar">
          {actions.includes('snooze') && card.task_id ? (
            <span className="zzwrap">
              <button
                type="button"
                className="btn zz"
                onClick={() => setMenuOpen(menuOpen === 'zz' ? '' : 'zz')}
              >
                Snooze
              </button>
              {menuOpen === 'zz' ? (
                <div className="morelist" data-testid="snooze-menu">
                  <div onClick={() => submitSnooze('next')}>Until the next document</div>
                  <div onClick={() => submitSnooze('7d')}>7 days</div>
                  <div onClick={() => submitSnooze('date')}>Pick date…</div>
                </div>
              ) : null}
            </span>
          ) : null}
          <span className="more">
            <button
              type="button"
              className="btn"
              onClick={() => setMenuOpen(menuOpen === 'more' ? '' : 'more')}
            >
              ⋯
            </button>
            {menuOpen === 'more' ? (
              <div className="morelist">
                <div onClick={() => setMenuOpen('')}>Leave open</div>
              </div>
            ) : null}
          </span>
        </div>
      </div>
    )
  }

  /* ── reconcile card ────────────────────────────────────────────── */
  if (card.stage === 'reconcile') {
    const lg = card.ledger_context
    const plug = card.plug
    const why = card.why
    const noBalance = !lg?.balance
    const preExisting = actions.includes('confirm_pre_existing')
    const guidanceKey =
      card.shape === 'derived_balance'
        ? 'deck.derived_balance'
        : preExisting
          ? 'deck.pre_existing'
          : plug
            ? 'deck.plug'
            : 'deck.supply_gap'
    const bookVerb = preExisting
      ? submitConfirmPreExisting
      : plug?.bookable !== false && actions.includes('book_plug')
        ? submitBookPlug
        : null
    return (
      <div className={`card popin${editing ? ' editing' : ''}`} data-card-id={card.card_id}>
        <div className="ctit">
          <span className={kClass}>{stageLabel}</span>
          <span className="anch">{card.anchor_label}</span>
          {why ? (
            <button
              type="button"
              className="whychip"
              onClick={() =>
                window.alert(
                  [
                    humanizeTokensIn(why.cause),
                    why.reason,
                    why.stored ? `stored: ${formatNumberString(why.stored)}` : null,
                    why.computed ? `computed: ${formatNumberString(why.computed)}` : null,
                    why.delta ? `Δ: ${formatNumberString(why.delta)}` : null,
                    why.balance_as_of ? `balance as of ${why.balance_as_of}` : null,
                    why.latest_trade_date ? `latest trade ${why.latest_trade_date}` : null,
                  ]
                    .filter(Boolean)
                    .join('\n')
                )
              }
            >
              Why ?
            </button>
          ) : null}
          <HelpDot k={guidanceKey} />
        </div>
        <div className="hd">
          <span>movements</span>
          <span>date</span>
          <span>amount</span>
          <span></span>
        </div>
        {movementRows.map(ledgerRow)}
        {lg?.movements_sum != null ? (
          <div className="ln sum">
            <span className="lbl">to {lg.movements_to ?? 'date'}</span>
            <span className="dt"></span>
            <span className="val">{lg.movements_sum}</span>
            <span></span>
          </div>
        ) : null}
        <div className="hd">
          <span>balance</span>
          <span>as of</span>
          <span>amount</span>
          <span></span>
        </div>
        {/* v52: no difference row — the ✗ verdict rides the end column
         * of the balance line; the residual figure renders ONLY on the
         * closes-with plug row below. */}
        {noBalance ? (
          <div className="ln rec" data-testid="no-balance-line">
            <span className="lbl">
              <span className="sdot d-rec" />
              <span style={{ marginLeft: 9 }}>no balance record</span>
            </span>
            <span className="dt"></span>
            <span className="val"></span>
            <span className="st4 miss" data-testid="match-mark">
              ✗
            </span>
          </div>
        ) : (
          <div className="ln rec">
            <span className="lbl">
              <span className="sdot d-rec" />
              <span style={{ marginLeft: 9 }}>Recorded balance</span>
            </span>
            <span className="dt">{lg?.balance_as_of ?? ''}</span>
            <span className="val">{lg?.balance}</span>
            <span className="st4 miss" data-testid="match-mark">
              ✗
            </span>
          </div>
        )}
        {plug ? (
          <div className="plugz" data-testid="plug-zone">
            <div className="ln bkr">
              <span className="lbl">
                <input
                  type="checkbox"
                  className="bk"
                  checked={isChecked('plug')}
                  onChange={() => setChecked((p) => ({ ...p, plug: !isChecked('plug') }))}
                />
                <span className="rjx" onClick={() => toggleReject('plug')}>
                  ✕
                </span>
                <span className="sdot d-inf" />
                <span style={{ marginLeft: 9 }}>{plug.label}</span>
              </span>
              <span className="dt">{lg?.balance_as_of ? `by ${lg.balance_as_of}` : ''}</span>
              <span className="val">
                {formatNumberString(
                  plug.units_delta ??
                    (card.shape === 'derived_balance' ? (lg?.movements_sum ?? '') : '')
                )}
              </span>
              <span></span>
            </div>
          </div>
        ) : null}
        {card.evidence_note ? (
          /* Ale ruling 2026-08-17: a derived balance is the officer's own
           * arithmetic, never evidence. Booking it RAISES the balance
           * obligation and lets the chase go out — so the card has to say,
           * in words and on the card, that the paper is still owed. Without
           * this line an officer books an inference believing they verified
           * it. */
          <div className="books" data-testid="evidence-note">
            {card.evidence_note}
          </div>
        ) : null}
        {plug ? (
          <div className="books">
            {card.shape === 'derived_balance' ? (
              <>
                Books: <b>derived balance</b>.
              </>
            ) : preExisting ? (
              <>
                Books: <b>inception balance</b> — the record's starting line. Its evidence is the
                balance document; nothing is requested from the counterparty.
              </>
            ) : (
              <>
                Books: <b>inferred {plug.transaction_type?.replace(/_/g, ' ') ?? 'trade'}</b> · its
                document obligation opens on the booked entry.
              </>
            )}
          </div>
        ) : (
          <div className="books">
            The residual cannot name a holder — nothing to book. Snooze until the document lands.
          </div>
        )}
        {attestOpen ? (
          <div className="reasonbox" data-testid="attestation-box">
            <textarea
              data-testid="attestation-input"
              placeholder="note — why this balance is the record's starting line"
              value={attestation}
              onChange={(e) => setAttestation(e.target.value)}
            />
            {!attestation.trim() ? (
              <span className="reasonhint" data-testid="reason-required">
                the attestation is required — it is recorded under your name
              </span>
            ) : null}
          </div>
        ) : null}
        {errorBlock}
        <div className="abar">
          {bookVerb ? (
            <button
              type="button"
              className="btn p apv"
              disabled={busy || (preExisting && attestOpen && !attestation.trim())}
              onClick={bookVerb}
            >
              {preExisting ? 'Book inception balance' : 'Book'}
            </button>
          ) : null}
          {actions.includes('snooze') && card.task_id ? (
            <span className="zzwrap">
              <button
                type="button"
                className="btn zz"
                onClick={() => setMenuOpen(menuOpen === 'zz' ? '' : 'zz')}
              >
                Snooze
              </button>
              {menuOpen === 'zz' ? (
                <div className="morelist" data-testid="snooze-menu">
                  <div onClick={() => submitSnooze('next')}>Until the next document</div>
                  <div onClick={() => submitSnooze('7d')}>7 days</div>
                  <div onClick={() => submitSnooze('date')}>Pick date…</div>
                </div>
              ) : null}
            </span>
          ) : null}
          {plug && bookVerb && !preExisting ? (
            <>
              <button type="button" className="btn edt" onClick={() => setEditing(true)}>
                Edit
              </button>
              <button
                type="button"
                className="btn p sel"
                disabled={busy || !isChecked('plug') || isRejected('plug')}
                onClick={submitBookPlug}
              >
                Approve selected
              </button>
              <button type="button" className="btn cnc" onClick={cancelEdit}>
                Cancel
              </button>
            </>
          ) : null}
          <span className="more">
            <button
              type="button"
              className="btn"
              onClick={() => setMenuOpen(menuOpen === 'more' ? '' : 'more')}
            >
              ⋯
            </button>
            {menuOpen === 'more' ? (
              <div className="morelist">
                <div onClick={() => setMenuOpen('')}>Leave open</div>
              </div>
            ) : null}
          </span>
        </div>
      </div>
    )
  }

  /* ── book card: field-change grid (attributes only) ────────────── */
  if (isFieldCard) {
    return (
      <div className={`card popin${editing ? ' editing' : ''}`} data-card-id={card.card_id}>
        <div className="ctit">
          <span className={kClass}>{stageLabel}</span>
          <span className="anch">{card.anchor_label}</span>
          {langChip}
          <HelpDot k="deck.fields" />
          {srcCount > 0 ? (
            <button type="button" className="opensrc" onClick={openCardSource}>
              {srcCount > 1 ? `Open sources (${srcCount}) 📄` : 'Open source 📄'}
            </button>
          ) : null}
        </div>
        <div className="hd fg">
          <span>field</span>
          <span style={{ textAlign: 'left' }}>recorded</span>
          <span></span>
          <span style={{ textAlign: 'left' }}>
            proposed <span className="sdot d-upd" style={{ marginLeft: 4 }} />
          </span>
        </div>
        {fieldRows.map((r) => (
          <div key={r.row_id}>
            <div className={lnClass(r, 'fg')}>
              <span className="fname">
                {bkr(r)}
                {r.label}
                {chip(r)}
              </span>
              <span className="old">{r.recorded_value ?? '— not stated'}</span>
              <span className="arr">→</span>
              <span className="new">{r.proposed_value ?? ''}</span>
            </div>
            {isRejected(r.row_id) ? reasonBox(r.row_id, `why “${r.label}” is rejected`) : null}
          </div>
        ))}
        {booksSummary ? <div className="books">{booksSummary}</div> : null}
        {discardBlock}
        {errorBlock}
        <div className="abar">
          <button
            type="button"
            className="btn p apv"
            disabled={busy}
            onClick={() => submitApprove(false)}
          >
            Approve
          </button>
          <button type="button" className="btn edt" onClick={() => setEditing(true)}>
            Edit
          </button>
          <button
            type="button"
            className="btn p sel"
            disabled={busy || reasonsMissing}
            onClick={() => submitApprove(true)}
          >
            Approve selected
          </button>
          {discardButton}
          <button type="button" className="btn cnc" onClick={cancelEdit}>
            Cancel
          </button>
          <span className="more">
            <button
              type="button"
              className="btn"
              onClick={() => setMenuOpen(menuOpen === 'more' ? '' : 'more')}
            >
              ⋯
            </button>
            {menuOpen === 'more' ? (
              <div className="morelist">
                <div onClick={() => setMenuOpen('')}>Leave open</div>
              </div>
            ) : null}
          </span>
        </div>
      </div>
    )
  }

  /* ── book card: ledger grammar ─────────────────────────────────── */
  return (
    <div className={`card popin${editing ? ' editing' : ''}`} data-card-id={card.card_id}>
      <div className="ctit">
        <span className={kClass}>{stageLabel}</span>
        <span className="anch">{card.anchor_label}</span>
        {langChip}
        <HelpDot k="deck.ledger" />
        {srcCount > 0 ? (
          <button type="button" className="opensrc" onClick={openCardSource}>
            {srcCount > 1 ? `Open sources (${srcCount}) 📄` : 'Open source 📄'}
          </button>
        ) : null}
      </div>
      {movementRows.length ? (
        <>
          <div className="hd">
            <span>movements</span>
            <span>date</span>
            <span>amount</span>
            <span></span>
          </div>
          {movementRows.map(ledgerRow)}
        </>
      ) : null}
      {balanceRows.length ? (
        <>
          <div className="hd">
            <span>balance</span>
            <span>as of</span>
            <span>amount</span>
            <span></span>
          </div>
          {balanceRows.map(ledgerRow)}
        </>
      ) : null}
      {allObjects.length ? (
        <div className="objs">
          <div className="hd">
            <span>objects</span>
            <span>date</span>
            <span></span>
          </div>
          {allObjects.map((o) => (
            <div key={o.row_id}>
              <div
                className={lnClass(o)}
                onClick={() => setOpenObjects((prev) => ({ ...prev, [o.row_id]: !prev[o.row_id] }))}
              >
                <span className="lbl">
                  {o.bookable && o.proposal_id ? bkr(o) : null}
                  <span className={`sdot ${STATE_DOT[o.state]}`} />
                  <span style={{ marginLeft: 9 }}>
                    {openObjects[o.row_id] ? '▾' : '▸'} <b>{tl(o.label)}</b>
                  </span>
                  {chip(o)}
                  {/* Ale ruling 2026-08-19: founding verb only in
                   * UNMATCHED-fund contexts — a deck already filed against
                   * a fund has had its founding decision; offering
                   * Mark-as-fund on its portfolio companies is the wrong-
                   * click trap (no undo). */}
                  {o.fund_vehicle_candidate &&
                  o.proposal_id &&
                  deck.group_id &&
                  (deck.fund_ids ?? []).length === 0 ? (
                    <button
                      type="button"
                      className="markfund"
                      data-testid="rk-mark-fund"
                      disabled={busy}
                      title="Make this entity the Fund itself — the top of the ownership chain. Use it when the fund vehicle was not recognised automatically."
                      onClick={(e) => {
                        e.stopPropagation()
                        void markFund(o)
                      }}
                    >
                      Mark as fund
                    </button>
                  ) : null}
                  {/* Demo-v2 W1: the register replaces the founding verb —
                   * a fund-shaped row with no agreement gets a read-only
                   * label; the register is the only door. */}
                  {o.register_missing ? (
                    <span
                      className="regmissing"
                      data-testid="rk-register-missing"
                      title="no agreement on the register for this fund — update the register"
                    >
                      no agreement on register
                    </span>
                  ) : null}
                </span>
                <span className="dt">{o.date ?? ''}</span>
                <span className="val"></span>
              </div>
              {openObjects[o.row_id] && o.proposed_value ? (
                <div className="ofields">
                  <div className="frow">
                    <span className="k">{o.field ?? 'details'}</span>
                    <span>{o.proposed_value}</span>
                  </div>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
      {issuerPanel}
      {booksSummary ? <div className="books">{booksSummary}</div> : null}
      {discardBlock}
      {errorBlock}
      {/* A card whose rows are all settled (e.g. the discarded split of a
          multi-segment deck) must not offer a live Approve — the click
          would no-op silently (real-browser walk find, 2026-08-31). Say
          what happened and point at the open work instead. */}
      {bookableRows.length === 0 ? (
        <div className="abar" data-testid="rk-card-settled-bar">
          <span className="books">
            Nothing left to book on this card — its rows are settled.
            {deck.linked_decks?.length ? ' The open work is on the linked split.' : ''}
          </span>
        </div>
      ) : (
        <div className="abar">
          <button
            type="button"
            className="btn p apv"
            disabled={busy}
            onClick={() => submitApprove(false)}
          >
            Approve docs
          </button>
          <button type="button" className="btn edt" onClick={() => setEditing(true)}>
            Edit
          </button>
          <button
            type="button"
            className="btn p sel"
            disabled={busy || reasonsMissing}
            onClick={() => submitApprove(true)}
          >
            Approve selected
          </button>
          {discardButton}
          <button type="button" className="btn cnc" onClick={cancelEdit}>
            Cancel
          </button>
          <span className="more">
            <button
              type="button"
              className="btn"
              onClick={() => setMenuOpen(menuOpen === 'more' ? '' : 'more')}
            >
              ⋯
            </button>
            {menuOpen === 'more' ? (
              <div className="morelist">
                <div onClick={() => setMenuOpen('')}>Leave open</div>
              </div>
            ) : null}
          </span>
        </div>
      )}
    </div>
  )
}
